import {customElement, property, state} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {Interval} from "../../../common/interval";
import {resolve} from "../../../container";
import {AutoInitializing, UnLitElement, WithEopSlots} from "../../../common/elements";
import {ManagingResources} from "../../../common/lifetime";
import {EopSliderElement} from "../eopSliderElement";
import {OUT_OF_VIEW_ATTR} from "../image/imageUtils";
import {HorizontalSwipeHandler} from "../../../common/utils/horizontalSwipeHandler";
import {Dictionary} from "../dictionary";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";

@customElement("eop-content-slider")
export class EopContentSlider extends WithEopSlots(AutoInitializing(ManagingResources(UnLitElement))) {

    @property({attribute: "slider-element-slot-name"})
    private sliderElementSlotName: string;
    @property({attribute: "baseplate-enabled", type: Boolean})
    private baseplateEnabled: boolean = false;
    @property({attribute: "autoslide-interval"})
    private autoslideInterval: number;
    @property({attribute: "number-of-elements", type: Number})
    private numberOfElements: number;
    @state()
    private activeIndex: number = 0;
    private autoslidePromise: Promise<void>;
    private dictionary: Dictionary;

    public constructor(private interval: Interval = resolve(Interval)) {
        super();

        new HorizontalSwipeHandler([this])
            .onSwipeLeft((_e) => this.next())
            .onSwipeRight((_e) => this.previous())
            .activate();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);

        if (this.autoslideInterval > 0) {
            this.autoslidePromise = this.interval.repeat(() => this.next(), this.autoslideInterval * 1000);

            this.addEventListener("click", () => {
                this.interval.cancel(this.autoslidePromise);
            }, {once: true});
            this.addEventListener("touchstart", () => {
                this.interval.cancel(this.autoslidePromise);
            }, {once: true});
        }
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.interval.cancel(this.autoslidePromise);
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.sliderElements.forEach((sliderElement) => {
            new EopSliderElement(sliderElement).image?.setAttribute(OUT_OF_VIEW_ATTR, "");
        });
        this.toggleActivationClass();
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);
        this.classList.toggle("reverse-background", this.baseplateEnabled);
    }

    public render(): TemplateResult {
        return html`
            <div class="content-slider" ?baseplate=${this.baseplateEnabled}>
                <div class="content-slider-elements">
                    ${this.resolveSlots(this.sliderElementSlotName)}
                </div>
                ${this.renderButtons()}
                ${this.renderIndicators()}
            </div>
        `;
    }

    private get sliderElements(): HTMLElement[] {
        return Array.from(this.querySelectorAll<HTMLElement>("[eop-slot-resolved='true']"))
            .filter(element => element.getAttribute("eop-slot") === this.sliderElementSlotName);
    }

    private renderButtons(): TemplateResult {
        const prevSlideLabel = this.dictionary.translate("MSG_PREVIOUS_SLIDE");
        const nextSlideLabel = this.dictionary.translate("MSG_NEXT_SLIDE");
        return html`
            <button @click=${this.previous} class="prev-button" type="button" aria-label=${prevSlideLabel}
                    ${trackingButtonInteract(prevSlideLabel)}
            ></button>
            <button @click=${this.next} class="next-button" type="button" aria-label=${nextSlideLabel}
                    ${trackingButtonInteract(nextSlideLabel)}
            ></button>
        `;
    }

    private renderIndicators(): TemplateResult {
        const indicatorTemplates = [];
        for (let i = 0; i < this.numberOfElements; i++) {
            const slideLabel = "slide " + (i + 1);
            indicatorTemplates.push(html`
                <button @click=${this.indicator}
                        data-index=${i}
                        class="${i === this.activeIndex ? "active" : ""} indicator"
                        aria-label=${slideLabel}
                        ${trackingButtonInteract(slideLabel)}
                ></button>
            `);
        }
        return html`
            <div class="indicators">
                ${indicatorTemplates}
            </div>`;
    }

    private previous(): void {
        this.toggleActivationClass();
        this.activeIndex = (this.activeIndex - 1 + this.numberOfElements) % this.numberOfElements;
        this.toggleActivationClass();
    }

    private next(): void {
        this.toggleActivationClass();
        this.activeIndex = (this.activeIndex + 1) % this.numberOfElements;
        this.toggleActivationClass();
    }

    private indicator(e: Event): void {
        this.toggleActivationClass();
        this.activeIndex = (e.target as Element).getAttribute("data-index")!.toInt();
        this.toggleActivationClass();
    }

    private toggleActivationClass(): void {
        const activeElement = this.sliderElements[this.activeIndex];
        if (activeElement.classList.contains("active")) {
            activeElement.classList.remove("active");
            activeElement.classList.add("inactive");
        } else {
            new EopSliderElement(activeElement).image?.removeAttribute(OUT_OF_VIEW_ATTR);
            activeElement.classList.remove("inactive");
            activeElement.classList.add("active");
        }
    }
}